import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { api } from "../../../../utils/api";
import FeedbackTable from "../widgets/FeedbackTable";
import TranscriptPlayer from "../widgets/TranscriptPlayer";
import CallControls from "../widgets/CallControls";
import CallStatusBanner from "../widgets/CallStatusBanner";
import { startAssistant, stopAssistant } from "../../../../utils/vapiClient";
import { DEPLOYMENT_CONFIG } from "../../../../config/deployment";

function generateGUID() {
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

const getRingtoneUrl = () => {
	const basePath = DEPLOYMENT_CONFIG.getCurrentPath();
	return process.env.REACT_APP_RINGTONE_URL ||
		`${basePath}/ringtone/professional-ringtone.mp3`;
};

const ReviewsTab = ({ user }) => {
	// ---- Admin feedback bindings ----
	const [items, setItems] = useState([]);
	const [filteredItems, setFilteredItems] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [loading, setLoading] = useState(false);

	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [audioUrl, setAudioUrl] = useState("");
	const [transcriptUrl, setTranscriptUrl] = useState("");
	const [callsummary, setCallsummary] = useState("");
	const [sidebarDownload, setSidebarDownload] = useState({});

	const fetchFeedback = async () => {
		setLoading(true);
		try {
			const res = await api.get("api/feedback");
			if (res.ok) {
				const list = res.data.items || [];
				setItems(list);
				setFilteredItems(list);
			} else toast.error("Failed to load trainee feedback");
		} catch (e) {
			console.error(e);
			toast.error("An error occurred while loading feedback");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchFeedback();
	}, []);

	// 🔍 Handle search filtering
	useEffect(() => {
		const term = searchTerm.toLowerCase();
		if (!term) {
			setFilteredItems(items);
		} else {
			setFilteredItems(
				items.filter(
					(f) =>
						f.traineeName?.toLowerCase().includes(term) ||
						f.traineeEmail?.toLowerCase().includes(term) ||
						f.callType?.toLowerCase().includes(term) ||
						f.feedback?.toLowerCase().includes(term) ||
						f.feedbackId?.toLowerCase().includes(term),
				),
			);
		}
	}, [searchTerm, items]);

	const handleDelete = (deletedItem) => {
		setItems((prev) => prev.filter((i) => i._id !== deletedItem._id));
		setFilteredItems((prev) => prev.filter((i) => i._id !== deletedItem._id));
	};

	const handlePlay = (row) => {
		if (!row.callRecordFileinfo) return;
		const base = process.env.REACT_APP_BASE_API_URL;
		const audioUrl = `${base}/api/calls/files?path=${encodeURIComponent(
			row.callRecordFileinfo,
		)}`;
		const transcriptPath =
			row.artifactTranscriptFilePath || row.transcriberJsonFilePath;
		const transcriptUrl = transcriptPath
			? `${base}/api/calls/files?path=${encodeURIComponent(transcriptPath)}`
			: "";
		const callsummary = row.callsummary || "";
		const transcriberHref = row.transcriberFilePath
			? `${base}/api/calls/files?path=${encodeURIComponent(
					row.transcriberFilePath,
			  )}`
			: null;
		const transcriberTitle = row.transcriberFilePath
			? row.transcriberFilePath.split(/[\\/]/).pop()
			: "";
		const callRecordHref = audioUrl;
		const callRecordTitle = row.callRecordFileinfo
			? row.callRecordFileinfo.split(/[\\/]/).pop()
			: "";

		const basePath = DEPLOYMENT_CONFIG.getCurrentPath();
		const params = new URLSearchParams({
			audioUrl,
			transcriptUrl,
			callsummary,
			transcriberHref: transcriberHref || "",
			transcriberTitle: transcriberTitle || "",
			callRecordHref,
			callRecordTitle,
		});
		const shareableLink = `${
			window.location.origin
		}${basePath}/call?${params.toString()}`;

		setAudioUrl(audioUrl);
		setTranscriptUrl(transcriptUrl);
		setCallsummary(callsummary);
		setSidebarDownload({
			transcriberHref,
			transcriberTitle,
			callRecordHref,
			callRecordTitle,
			shareableLink,
		});
		setSidebarOpen(true);
	};

	// ---- Call panel logic ----
	const [callType, setCallType] = useState("Anytime Script");
	const [customerMood, setCustomerMood] = useState("Happy");
	const [gender, setGender] = useState("M");
	const [issue, setIssue] = useState("");
	const [callState, setCallState] = useState({
		active: false,
		callId: null,
		starting: false,
		stopping: false,
	});
	const [feedbackId, setFeedbackId] = useState(generateGUID());
	const [fieldsDisabled, setFieldsDisabled] = useState(false);
	const ringtoneRef = useRef(null);

	useEffect(() => {
		const updateVapiAssistant = async () => {
			const assistantId = getAssistantIdForCallType(callType);
			if (!assistantId) return;

			const payload = {
				recordingEnabled: true,
				server: {
					url: "https://ringringcloser.com/api/api/calls/vapi/webhook",
					timeoutSeconds: 20,
				},
				serverMessages: ["end-of-call-report"],
			};

			try {
				const apiKey =
					process.env.REACT_APP_VAPI_PRIVATE_API_KEY ||
					"b3914ca8-b10b-4a5f-bf67-12f688e332b3";
				await fetch(`https://api.vapi.ai/assistant/${assistantId}`, {
					method: "PATCH",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${apiKey}`,
					},
					body: JSON.stringify(payload),
				});
			} catch (err) {
				console.error("Vapi API error:", err);
			}
		};

		updateVapiAssistant();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [callType]);

	const getAssistantIdForCallType = (type) => {
		switch (type) {
			case "Anytime Script":
				return process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT || "";
			case "Anytime Script - L1":
				return (
					process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT_L1 ||
					"cecbb4ff-770b-47c9-83ef-e999aaad8645"
				);
			case "Anytime Script - L2":
				return (
					process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT_L2 ||
					"e469bc10-2ef6-4935-b16d-ff2be4feacb9"
				);
			case "Anytime Script - L3":
				return (
					process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT_L3 ||
					"29fca220-e33f-482e-b214-ebde1a6f7a00"
				);
			case "Anytime Script - L4":
				return (
					process.env.REACT_APP_VAPI_ASSISTANT_ID_ANYTIME_SCRIPT_L4 ||
					"98cb6512-91b1-4ee3-bdf6-ed804ad90e9a"
				);
			case "ISO":
				return process.env.REACT_APP_VAPI_ASSISTANT_ID_ISO || "";
			case "PMI Additionals":
				return process.env.REACT_APP_VAPI_ASSISTANT_ID_PMI_ADDITIONALS || "";
			case "MAINT Maintenance":
				return process.env.REACT_APP_VAPI_ASSISTANT_ID_MAINT_MAINTENANCE || "";
			default:
				return process.env.REACT_APP_VAPI_ASSISTANT_ID || "";
		}
	};

	const playRingtone = () => {
		if (ringtoneRef.current) {
			ringtoneRef.current.currentTime = 0;
			ringtoneRef.current.play();
		}
	};

	const stopRingtone = () => {
		if (ringtoneRef.current) {
			ringtoneRef.current.pause();
			ringtoneRef.current.currentTime = 0;
		}
	};

	const startVapiCall = async () => {
		try {
			setCallState((s) => ({ ...s, starting: true }));
			const newFeedbackId = generateGUID();
			setFeedbackId(newFeedbackId);
			const assistantId = getAssistantIdForCallType(callType);
			playRingtone();
			await startAssistant(assistantId, {
				feedbackId: newFeedbackId,
				traineeId: user?.id || user?._id,
				traineeName: user?.name,
				traineeEmail: user?.email,
				callType,
				customerMood,
				issue: callType === "ISO" ? issue : undefined,
				score: 0,
				feedback: "",
			});
			setCallState({ active: true, starting: false, stopping: false });
			toast.success("Call started");
		} catch (err) {
			console.error(err);
			toast.error(err?.message || "Error starting call");
			setCallState((s) => ({ ...s, starting: false }));
			stopRingtone();
		}
	};

	const stopVapiCall = async () => {
		try {
			setCallState((s) => ({ ...s, stopping: true }));
			await stopAssistant();
			toast.info("Call stopped");
			setCallState({ active: false, starting: false, stopping: false });
			setFieldsDisabled(false);
		} catch (err) {
			console.error(err);
			toast.error(err?.message || "Error stopping call");
			setCallState((s) => ({ ...s, stopping: false }));
		}
	};

	const handleCallTypeChange = (newCallType) => {
		setCallType(newCallType);
		if (newCallType !== "ISO") setIssue("");
	};

	const handleRandomize = () => {
		const moods = [
			"Happy",
			"Skeptical",
			"Anxious",
			"Frustrated",
			"Disappointed",
			"Neutral",
			"Difficult",
		];
		const randomMood = moods[Math.floor(Math.random() * moods.length)];
		const randomGender = Math.random() < 0.5 ? "M" : "F";
		setCustomerMood(randomMood);
		setGender(randomGender);
		setFieldsDisabled(true);
	};

	return (
		<div className="tdb tdb-corp">
			<div className="tdb-grid">
				{/* LEFT — Start a Call */}
				<main className="tdb-main">
					<div className="card">
						<div
							className="card-header"
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
							}}
						>
							<h2 className="card-title">Start a Call</h2>
							<button
								className="btn btn-secondary"
								style={{
									display: "flex",
									alignItems: "center",
									gap: 6,
									fontSize: 14,
									padding: "4px 12px",
								}}
								onClick={handleRandomize}
								title="Randomize selections"
							>
								<span
									role="img"
									aria-label="dice"
								>
									🎲
								</span>{" "}
								Random
							</button>
						</div>

						<CallControls
							callType={callType}
							customerMood={customerMood}
							gender={gender}
							issue={issue}
							callState={callState}
							onChangeCallType={handleCallTypeChange}
							onChangeCustomerMood={setCustomerMood}
							onChangeGender={setGender}
							onChangeIssue={setIssue}
							onStart={startVapiCall}
							onStop={stopVapiCall}
							fieldsDisabled={fieldsDisabled}
						/>

						{callState.active && <CallStatusBanner callState={callState} />}
					</div>
				</main>

				{/* RIGHT — Feedback Table */}
				<aside className="tdb-side">
					<div className="card">
						<div className="card-header">
							<h2 className="card-title">Review &amp; Scores</h2>
							<div className="reviews-search-bar">
								<div className="reviews-search-wrapper">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="reviews-search-icon"
										fill="none"
										viewBox="0 0 24 24"
										strokeWidth={1.8}
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M21 21l-4.35-4.35m1.85-4.65a7 7 0 11-14 0 7 7 0 0114 0z"
										/>
									</svg>
									<input
										type="text"
										className="reviews-search-input"
										placeholder="Search by trainee, email, call type, feedback, or ID"
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
									/>
								</div>
							</div>
						</div>
						<div style={{ overflowX: "auto" }}>
							{loading ? (
								<div style={{ padding: 16, color: "#6b7280" }}>
									Loading feedback...
								</div>
							) : (
								<FeedbackTable
									items={filteredItems}
									onPlay={handlePlay}
									onDelete={handleDelete}
								/>
							)}
						</div>
					</div>
				</aside>
			</div>

			{/* Ringtone + Transcript overlay */}
			<audio
				ref={ringtoneRef}
				src={getRingtoneUrl()}
				preload="auto"
			/>
			{sidebarOpen &&
				createPortal(
					<TranscriptPlayer
						audioUrl={audioUrl}
						transcriptUrl={transcriptUrl}
						callsummary={callsummary}
						onClose={() => setSidebarOpen(false)}
						transcriberHref={sidebarDownload.transcriberHref}
						transcriberTitle={sidebarDownload.transcriberTitle}
						callRecordHref={sidebarDownload.callRecordHref}
						callRecordTitle={sidebarDownload.callRecordTitle}
						shareableLink={sidebarDownload.shareableLink}
					/>,
					document.body,
				)}
		</div>
	);
};

export default ReviewsTab;
